"use client";

import { useLocale } from "next-intl";
import { usePathname, useRouter, routing } from "@/i18n/routing";
import { useParams } from "next/navigation";
import { useEffect, useState } from "react";

export default function FloatingLanguageSwitcher() {
  const router = useRouter();
  const pathname = usePathname(); // next-intl 的 usePathname 返回不包含 locale 的路径
  const params = useParams();
  const [currentLocale, setCurrentLocale] = useState<string>(routing.defaultLocale);

  // 从多个来源获取当前 locale，确保准确性
  useEffect(() => {
    // 优先从 params 获取（最可靠）
    const localeFromParams = params?.locale as string;
    if (localeFromParams && routing.locales.includes(localeFromParams as any)) {
      setCurrentLocale(localeFromParams);
      return;
    }

    // 如果 params 没有，从 URL 路径中提取
    if (typeof window !== "undefined") {
      const path = window.location.pathname;

      for (const loc of routing.locales) {
        if (path === `/${loc}` || path.startsWith(`/${loc}/`)) {
          setCurrentLocale(loc);
          return;
        }
      }

      // 如果没有匹配到任何语言，使用默认语言
      setCurrentLocale(routing.defaultLocale);
    }
  }, [params, pathname]);

  // 只显示中英文，中文在前（默认语言）
  const languages = [
    { code: "zh", label: "中文" },
    { code: "en", label: "EN" },
  ];

  const switchLocale = (newLocale: string) => {
    // 获取当前实际路径
    // 优先使用 window.location.pathname（更可靠，特别是在 404 页面）
    let currentPath = "";
    if (typeof window !== "undefined") {
      currentPath = window.location.pathname;
    } else {
      currentPath = pathname || "/";
    }
    
    // 移除现有的 locale 前缀
    let targetPath = currentPath;
    for (const loc of routing.locales) {
      if (targetPath.startsWith(`/${loc}/`)) {
        targetPath = targetPath.slice(`/${loc}/`.length);
        break;
      } else if (targetPath === `/${loc}`) {
        targetPath = "/";
        break;
      }
    }
    
    // 确保路径以 / 开头
    if (!targetPath.startsWith("/")) {
      targetPath = `/${targetPath}`;
    }
    
    // 如果路径为空，使用根路径
    if (targetPath === "") {
      targetPath = "/";
    }
    
    // 使用 router.replace 切换语言，next-intl 会自动添加正确的 locale 前缀
    router.replace(targetPath, { locale: newLocale });
  };

  return (
    <div className="fixed top-4 left-4 z-50 flex gap-1 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-gray-200/50 p-1">
      {languages.map((lang) => {
        const isActive = currentLocale === lang.code;
        return (
          <button
            key={lang.code}
            onClick={() => switchLocale(lang.code)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
              isActive
                ? "bg-black text-white shadow-md"
                : "text-gray-700 hover:bg-gray-100"
            }`}
            aria-label={`切换到${lang.label}`}
          >
            {lang.label}
          </button>
        );
      })}
    </div>
  );
}

